/**
 * Searching, filtering and sorting a list of products.
 *
 * WHY THIS IS A LIB. Two surfaces browse products: the marketplace and a
 * seller's storefront. Both offer the same controls and a shopper who narrows a
 * search on one and repeats it on the other has every right to the same answer.
 * The whole of this file used to live inline in src/app/marketplace/page.tsx as
 * a chain of useMemos, which meant the storefront could only match it by being
 * a second hand-written copy of a hundred lines of predicate, and a second
 * copy of "which fields does search look at" is how a product becomes findable
 * on one page and invisible on the other.
 *
 * PURE ON PURPOSE. Nothing here touches React, Firestore or the DOM, so the
 * rules are testable on their own (src/lib/product-filters.test.ts) rather than
 * only through a 1000-line page component.
 *
 * MONEY IS IN AUD. `priceRange` is held in the currency products are actually
 * priced in, never in the viewer's display currency, so it survives a currency
 * switch and a shared URL unchanged. Converting for the two input boxes is the
 * caller's job; see the marketplace's `displayRangeValue`. Getting this
 * backwards is a real bug this codebase has had: a "€10 max" filter that was
 * really A$10 hid products the grid beside it was showing at under €10.
 */

import {
  CATALOG_TYPE_SLUGS,
  OTHER_SLUG,
  RESELL_RIGHTS_PURPOSE,
  catalogPurposeEntry,
  catalogTypeEntry,
  isCatalogPurpose,
  isCatalogType,
  taxonomyTagMatch,
} from './catalog-taxonomy';
import { inferCatalogTaxonomy } from './catalog-taxonomy-inference';
import {
  TEACHING_PURPOSE,
  categoryFieldsFor,
  hasCategoryFields,
  hasYearLevel,
  matchesCategoryFields,
  type CategoryFieldValues,
} from './category-fields';
import { isDiscountablePricingModel } from './discount-eligibility';
import { listingPriceAud } from './listing-price';
import { SALE_TAG, withoutPlatformTags } from './platform-tags';

/** The orderings a browse surface offers. */
export type ProductSort = 'best-selling' | 'newest' | 'top-rated' | 'price-asc' | 'price-desc';

export const SORT_VALUES: readonly ProductSort[] = [
  'best-selling',
  'newest',
  'top-rated',
  'price-asc',
  'price-desc',
];

export const SORT_LABELS: Readonly<Record<ProductSort, string>> = {
  'best-selling': 'Best selling',
  newest: 'Newest',
  'top-rated': 'Top rated',
  'price-asc': 'Price: Low to High',
  'price-desc': 'Price: High to Low',
};

export type ProductRecency = 'all-time' | 'past-day' | 'past-week' | 'past-month' | 'past-year' | 'custom';

export const RECENCY_VALUES: readonly ProductRecency[] = [
  'all-time',
  'past-day',
  'past-week',
  'past-month',
  'past-year',
  'custom',
];

export const RECENCY_LABELS: Readonly<Record<ProductRecency, string>> = {
  'all-time': 'All time',
  'past-day': 'Past 24 hours',
  'past-week': 'Past week',
  'past-month': 'Past month',
  'past-year': 'Past year',
  custom: 'Custom range',
};

/** Days back each named window covers. `custom` and `all-time` are not windows. */
const RECENCY_DAYS: Readonly<Record<string, number>> = {
  'past-day': 1,
  'past-week': 7,
  'past-month': 30,
  'past-year': 365,
};

export type ProductFilterState = {
  search: string;
  tags: string[];
  fileTypes: string[];
  /**
   * `[min, max]` in AUD. `Infinity` as the max means "no ceiling", which is
   * distinct from "the most expensive product on this page": a ceiling pinned
   * to the current maximum would silently start excluding things the moment a
   * dearer product is published.
   */
  priceRange: [number, number];
  /** Minimum stars, 0 for no minimum. */
  rating: number;
  recency: ProductRecency;
  /** Only consulted when `recency` is 'custom'. */
  customDateRange?: { from?: Date; to?: Date };
  /** A language code, or 'all'. */
  language: string;
  /**
   * The marketplace's purpose bar: one purpose slug (catalog-taxonomy.ts), or
   * null for All. Optional because only the marketplace has the bar; a
   * storefront leaves it out and filters on nothing.
   */
  purpose?: string | null;
  /** Catalogue type slugs ticked in the Filters menu; a product matches any of them. */
  types?: string[];
  /**
   * The category fields picked (category-fields.ts): for teaching, the year
   * levels, subjects, kinds, curricula and formats the Filters menu offers
   * while Education is the purpose. Any pick within a field, every field.
   */
  categoryFields?: CategoryFieldValues;
};

export function emptyFilterState(): ProductFilterState {
  return {
    search: '',
    tags: [],
    fileTypes: [],
    priceRange: [0, Infinity],
    rating: 0,
    recency: 'all-time',
    customDateRange: undefined,
    language: 'all',
    purpose: null,
    types: [],
    categoryFields: {},
  };
}

/**
 * Whether anything except the search box and the purpose bar is narrowing the
 * list.
 *
 * Both are deliberately excluded: each has its own visible state (the text in
 * the box, the pressed chip in the bar), so counting them would light up a
 * "filters active" dot for something the viewer can already see. Callers that
 * want "is the list narrowed at all" ask for all three, which is what the
 * marketplace's count line and empty state do.
 */
export function hasActiveFilters(state: ProductFilterState): boolean {
  return state.priceRange[0] !== 0
    || state.priceRange[1] !== Infinity
    || state.rating > 0
    || state.recency !== 'all-time'
    || state.tags.length > 0
    || state.fileTypes.length > 0
    || (state.types?.length ?? 0) > 0
    || hasCategoryFields(state.categoryFields)
    || (state.language !== 'all' && state.language !== '');
}

/** A minimal product shape. Deliberately loose: both callers pass richer objects.
 *
 * The pricing-model fields are here because `effectivePriceAud` has to reach
 * them: a tiered listing's price lives in `priceTiers`, a PWYW listing's in
 * `pwywMinPrice`, and a subscription's in `subscriptionPrice`. See
 * src/lib/listing-price.ts. */
type FilterableProduct = {
  id?: string;
  name?: string;
  shortDescription?: string;
  longDescription?: string;
  aiHint?: string;
  price?: number;
  discountedPrice?: number | null;
  pricingModel?: string | null;
  priceTiers?: ReadonlyArray<{ price?: unknown } | null | undefined> | null;
  pwywMinPrice?: number | null;
  pwywMinPriceEnabled?: boolean | null;
  subscriptionPrice?: number | null;
  /** Who sells it: the best-selling sort deals tied listings round the sellers. */
  sellerId?: string;
  rating?: number;
  sales?: number;
  tags?: string[];
  productTags?: string[];
  files?: Record<string, unknown>;
  details?: Record<string, unknown> | null;
  createdAt?: unknown;
  /** The seller's catalogue type and purposes (catalog-taxonomy.ts); see effectiveTaxonomyOf. */
  catalogType?: unknown;
  purpose?: unknown;
  secondaryPurpose?: unknown;
  /** The category fields as stored (category-fields.ts); see categoryFieldsOf. */
  categoryFields?: unknown;
};

/** The two fields a product's tags have ever been stored in. */
export type ProductTagFields = { productTags?: unknown; details?: unknown };

/**
 * A product's tags, from either of the two places they have been stored.
 *
 * `productTags` is the current home; `details.productTags` is where older
 * documents put them, and the create form still writes both. Both are read
 * because the marketplace has always read both, and dropping the fallback would
 * make every legacy product untaggable and unsearchable by tag on the same day.
 *
 * AN EMPTY TOP-LEVEL LIST IS AN ANSWER, NOT A MISS. This mirrors the original
 * `p.productTags || p.details?.productTags || []`, where `[]` is truthy and so
 * short-circuits the fallback, and the exactness matters: the EDIT form writes
 * only the top-level field (see the tag handler in
 * src/app/dashboard/products/edit/[id]/page.tsx), so a seller who removes every
 * tag leaves the old list sitting in `details`. Falling back on an empty array
 * would resurrect tags they deleted, on the marketplace, indefinitely.
 *
 * The parameter is deliberately looser than `FilterableProduct`: the
 * server-rendered catalog (src/lib/catalog-tags.ts) hands in raw Firestore
 * documents, and a product's tags must resolve the same way on a crawlable page
 * as they do in the marketplace grid.
 */
export function productTagsOf(product: ProductTagFields): string[] {
  const top = product.productTags;
  if (Array.isArray(top)) return top.filter((t): t is string => typeof t === 'string');
  const nested = (product.details as { productTags?: unknown } | null | undefined)?.productTags;
  if (Array.isArray(nested)) return nested.filter((t): t is string => typeof t === 'string');
  return [];
}

/**
 * Whether the listing is on sale right now: a model the server applies a
 * discount to (discount-eligibility.ts) and a `discountedPrice` below its
 * `price`, which is the comparison resolveItemPriceAud and currentLinePriceAud
 * make. Read off whatever the caller holds, so a product run through the live
 * discount overlay answers by the window, exactly as its card's strike-through
 * does, and a raw document answers by the server's mirror.
 */
export function isOnSale(product: Pick<FilterableProduct, 'pricingModel' | 'price' | 'discountedPrice'>): boolean {
  if (!isDiscountablePricingModel(product.pricingModel)) return false;
  const dp = product.discountedPrice;
  return dp !== undefined && dp !== null && Number.isFinite(Number(dp)) && Number(dp) < Number(product.price);
}

/**
 * A tag list with the sale marker decided by the product rather than by the
 * list: any platform tag it carries removed, and "discounted" added exactly
 * when the listing is on sale.
 */
function withLiveSaleTag(product: FilterableProduct, tags: string[]): string[] {
  const own = withoutPlatformTags(tags);
  return isOnSale(product) ? [...own, SALE_TAG] : own;
}

/**
 * The tags the browse FILTERS and FACETS match: the seller's tags, plus
 * "discounted" exactly while the listing is on sale.
 *
 * Not the stored "discounted" marker. The discount engine writes that into
 * `productTags`, a list the seller also writes, so a seller could tag a
 * full-price listing "discounted" (the tag picker even offered it) and sit in
 * the marketplace's discounted filter, and a seller who removed it dropped a
 * real sale out of it. The marker also trails a window by up to a minute. The
 * product's own price is what the card shows, so the filter reads that.
 *
 * Nor a word that names a catalogue type or purpose ("templates",
 * "marketing"): those are fields, offered by the Type filter and the purpose
 * bar, and an old tag carrying one is dropped here as on every public page
 * (catalogTagsOf in catalog-tags.ts). The guess still reads them as evidence
 * (effectiveTaxonomyOf reads productTagsOf), and search still finds them
 * through the product's type and purposes (taxonomySearchWords).
 */
export function filterTagsOf(product: FilterableProduct): string[] {
  return withLiveSaleTag(product, productTagsOf(product).filter((tag) => taxonomyTagMatch(tag) === null));
}

/**
 * The tags SEARCH matches against.
 *
 * `filterTagsOf` plus one last fallback to `product.tags`, which is what the
 * marketplace's search chain has always done. That field is the RIBBON list
 * (featured, boosted, growing, discounted, secure), not the seller's tags, so
 * it is reached only when a product carries neither real tag field, never
 * unioned in, or every featured product on the site would match a search for
 * "featured". The sale marker is decided by the price on that path too.
 */
function searchTagsOf(product: FilterableProduct): string[] {
  if (Array.isArray(product.productTags)) return filterTagsOf(product);
  const nested = (product.details as { productTags?: unknown } | null | undefined)?.productTags;
  if (Array.isArray(nested)) return filterTagsOf(product);
  const ribbon = Array.isArray(product.tags) ? product.tags.filter((t): t is string => typeof t === 'string') : [];
  return withLiveSaleTag(product, ribbon);
}

/**
 * Every file extension among a product's deliverables, lowercased.
 *
 * Not deduped: the facet counter dedupes per product (a product with nine PNGs
 * should count once towards ".png"), while the filter only asks whether an
 * extension is present at all. Deduping here would be free for the second and
 * wrong for nothing, but the two callers are clearer asking for what they want.
 */
export function productFileExtsOf(product: FilterableProduct): string[] {
  const exts: string[] = [];
  const files = product.files;
  if (!files) return exts;
  for (const group of Object.values(files)) {
    if (!Array.isArray(group)) continue;
    for (const file of group) {
      const name = (file as { name?: unknown } | null)?.name;
      if (typeof name !== 'string') continue;
      const ext = name.split('.').pop()?.toLowerCase();
      if (ext) exts.push(ext);
    }
  }
  return exts;
}

/** Every deliverable's file name, from the legacy `files` map productFileExtsOf reads. */
function productFileNamesOf(product: FilterableProduct): Array<{ name: string }> {
  const names: Array<{ name: string }> = [];
  for (const group of Object.values(product.files ?? {})) {
    if (!Array.isArray(group)) continue;
    for (const file of group) {
      const name = (file as { name?: unknown } | null)?.name;
      if (typeof name === 'string') names.push({ name });
    }
  }
  return names;
}

/** A product's catalogue type and purposes as every browse surface reads them. */
export type EffectiveTaxonomy = {
  /** A type slug: the seller's, else the guess, else Other. */
  catalogType: string;
  /** One or two purpose slugs, main first: the seller's, else the guess, else Other. */
  purposes: string[];
};

const taxonomyCache = new WeakMap<object, EffectiveTaxonomy>();

/**
 * The answers by what they were worked out from. The catalogue listener hands
 * over a new object for every product on every update (a sale anywhere), so
 * the object cache alone would guess the whole catalogue again each time.
 * Cleared when full, which only a very long session reaches.
 */
const taxonomyBySignature = new Map<string, EffectiveTaxonomy>();
const TAXONOMY_SIGNATURE_MAX = 5_000;

/** Everything effectiveTaxonomyOf reads, as one string. */
function taxonomySignature(product: FilterableProduct): string {
  return JSON.stringify([
    product.catalogType ?? null,
    product.purpose ?? null,
    product.secondaryPurpose ?? null,
    product.name ?? null,
    product.shortDescription ?? null,
    product.aiHint ?? null,
    productTagsOf(product),
    productFileNamesOf(product).map((file) => file.name),
    hasYearLevel(product.categoryFields),
  ]);
}

/**
 * The type and purposes a product is browsed by.
 *
 * The seller's choice where there is one, and a guess
 * (catalog-taxonomy-inference.ts) for each part that is missing or holds a
 * value outside the lists (a hand-edited document), so the listings saved
 * before the fields existed join the purpose bar without a migration and keep
 * that place until their seller saves a choice. A product the guess has nothing
 * on is Other, so every listing is under some chip.
 *
 * The guess reads only the fields the marketplace's server first screen reads
 * (MARKETPLACE_FIRST_SCREEN_FIELDS): the browser and the server must agree on
 * every product's shelf, or the bar reorders itself when the live catalogue
 * lands. Memoised on the product object, like the search haystack, and on its
 * inputs; the answer is frozen, since products share it.
 */
export function effectiveTaxonomyOf(product: FilterableProduct): EffectiveTaxonomy {
  const cached = taxonomyCache.get(product as object);
  if (cached) return cached;
  const signature = taxonomySignature(product);
  let result = taxonomyBySignature.get(signature);
  if (!result) {
    result = workOutTaxonomy(product);
    if (taxonomyBySignature.size >= TAXONOMY_SIGNATURE_MAX) taxonomyBySignature.clear();
    taxonomyBySignature.set(signature, result);
  }
  taxonomyCache.set(product as object, result);
  return result;
}

function workOutTaxonomy(product: FilterableProduct): EffectiveTaxonomy {
  // TEACHING NEEDS A YEAR LEVEL (owner, 2026-10-27). Teaching (the Education
  // slug) is for resources teachers use with their students, and a year level
  // is what one has and a guide for adults does not. Without one, Teaching is
  // dropped from the seller's choice and left out of the guess, so the shelf,
  // the purpose bar and /teaching hold teaching resources only. A main purpose
  // dropped that way hands over to the seller's second.
  const teaching = hasYearLevel(product.categoryFields);
  const choosable = (slug: unknown): slug is string => isCatalogPurpose(slug) && (teaching || slug !== TEACHING_PURPOSE);
  const droppedMain = product.purpose === TEACHING_PURPOSE && !teaching;
  const storedType = isCatalogType(product.catalogType) ? product.catalogType : null;
  const storedPurpose = choosable(product.purpose)
    ? product.purpose
    : droppedMain && choosable(product.secondaryPurpose) ? product.secondaryPurpose : null;
  const guess = storedType && storedPurpose
    ? null
    : inferCatalogTaxonomy({
      name: product.name,
      shortDescription: product.shortDescription,
      aiHint: product.aiHint,
      tags: productTagsOf(product),
      files: productFileNamesOf(product),
      catalogType: storedType ?? undefined,
    }, teaching ? {} : { excludePurposes: [TEACHING_PURPOSE] });
  let purposes: string[];
  if (storedPurpose) {
    // A resale pack has one shelf, and Resell rights is never a second one, so
    // a PLR pack cannot sit on a topic's shelf through either field.
    const second = droppedMain ? null : product.secondaryPurpose;
    purposes = choosable(second) && second !== storedPurpose
      && second !== RESELL_RIGHTS_PURPOSE && storedPurpose !== RESELL_RIGHTS_PURPOSE
      ? [storedPurpose, second]
      : [storedPurpose];
  } else {
    purposes = guess && guess.purposes.length > 0 ? guess.purposes : [OTHER_SLUG];
  }
  return Object.freeze({
    catalogType: storedType ?? guess?.catalogType ?? OTHER_SLUG,
    purposes: Object.freeze(purposes) as string[],
  });
}

/**
 * A product's category fields as every browse surface reads them: the stored
 * ones, for the set its purposes switch on (categoryFieldsFor), so a product
 * moved off Education leaves the teaching filters with it.
 */
export function categoryFieldsOf(product: FilterableProduct): CategoryFieldValues {
  return categoryFieldsFor(product.categoryFields, effectiveTaxonomyOf(product).purposes);
}

/**
 * The words a product's type and purposes add to its search haystack: each
 * one's label and the tag spellings that name it, so "templates" still finds a
 * template once its seller's "templates" tag has moved into the type field.
 */
function taxonomySearchWords(product: FilterableProduct): string[] {
  const { catalogType, purposes } = effectiveTaxonomyOf(product);
  const entries = [catalogTypeEntry(catalogType), ...purposes.map(catalogPurposeEntry)];
  return entries.flatMap((entry) => (entry && entry.slug !== OTHER_SLUG
    ? [entry.label, ...entry.tagAliases.map((alias) => alias.replace(/-/g, ' '))]
    : []));
}

/**
 * When a product was released, in epoch ms, or 0 when that is unknowable.
 *
 * Four shapes reach this, and all four are real: a Firestore Timestamp with a
 * `.toDate()`, a Date (the context converts most of them), a plain
 * `{seconds, nanoseconds}` map (what a Timestamp becomes after a
 * JSON round trip through a localStorage draft), and a date string.
 *
 * `details.releaseDate` wins over `createdAt` because a seller can set it and
 * the sort is advertised as "newest", which is a claim about the product rather
 * than about when a document happened to be written.
 */
export function productReleaseMs(product: FilterableProduct): number {
  const raw = (product.details as { releaseDate?: unknown } | null | undefined)?.releaseDate
    ?? product.createdAt;
  if (!raw) return 0;
  if (typeof (raw as { toDate?: unknown }).toDate === 'function') {
    return (raw as { toDate: () => Date }).toDate().getTime();
  }
  if (raw instanceof Date) return raw.getTime();
  if (typeof raw === 'object' && typeof (raw as { seconds?: unknown }).seconds === 'number') {
    return (raw as { seconds: number }).seconds * 1000;
  }
  const parsed = new Date(raw as string | number);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

/**
 * The price this listing ADVERTISES, in AUD, and therefore the number the
 * price filter and the two price sorts have to work on.
 *
 * Delegates to `listingPriceAud` so this agrees with the card rendered beside
 * it. It used to be `discountedPrice || price || 0`, which is right for a
 * standard listing and wrong for every other model: tiered and PWYW listings
 * carry no meaningful top-level `price`, so they all evaluated to 0. A minimum
 * price of anything above zero therefore hid every one of them, and
 * "Price: Low to High" sorted them to the front as though they were free, while
 * the card next to them read "From A$25".
 */
export function effectivePriceAud(product: FilterableProduct): number {
  return listingPriceAud(product);
}

/**
 * The seed that orders listings with equal sales on the marketplace and
 * search: the UTC day, so the order turns over once a day, at 00:00 UTC
 * (10 or 11 am in Sydney).
 *
 * Computed ONCE, on the server, per page view, and handed to the browser with
 * the first screen. The server ranks the first screen with it and the browser
 * sorts the live catalogue with it, so the handover cannot reshuffle the grid,
 * not even when the browser's clock or a visit that spans midnight would put
 * the two on different days.
 */
export function browseMixSeed(at: Date): string {
  return at.toISOString().slice(0, 10);
}

/**
 * A 32-bit hash of a string: FNV-1a, then murmur3's finaliser so that strings
 * differing only in their last characters (a seed's day, an id's tail) still
 * land far apart. Integer arithmetic only, so Node and every browser agree on
 * it bit for bit.
 */
function mixHash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Code-unit order. Never localeCompare: the server's ICU and a browser's may disagree. */
function compareCodeUnits(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Best-selling, with listings that have the same number of sales dealt round
 * their sellers: one listing from each seller before any seller's second.
 *
 * WHY. With few sales most listings tie, and a stable sort left the tie in
 * whatever order the list arrived in (document id on the marketplace): one
 * fixed first page for everyone, and a seller with one listing could sit behind
 * Load More indefinitely. This is the interim answer until there is enough
 * data for a real ranking (the deferred weighted plan); as sales accumulate
 * they decide the order and the mix only settles real ties.
 *
 * HOW. Within each sales count, each seller's listings are shuffled by the
 * seed, and the k-th of them goes in round k. Rounds run in order, and inside
 * a round the sellers stand in one order for the whole seed, so a round never
 * opens with the seller that closed the previous one while anyone else is
 * left. A listing with no seller id counts as a seller of its own.
 *
 * DEPENDS ON THE LISTINGS AND THE SEED ALONE, never on the order they were
 * handed in, which is what lets the server's first screen and the browser's
 * live grid agree (see browseMixSeed). Published in /terms, /guidelines and
 * the support bot; public-copy.test.ts holds them together.
 */
function bestSellingMixed<P extends FilterableProduct>(list: P[], seed: string): P[] {
  const sellerOf = (p: P) => (p.sellerId ? `seller:${p.sellerId}` : `listing:${p.id ?? ''}`);
  const runs = new Map<string, P[]>();
  for (const p of list) {
    const key = `${p.sales ?? 0}|${sellerOf(p)}`;
    const run = runs.get(key);
    if (run) run.push(p);
    else runs.set(key, [p]);
  }
  const round = new Map<P, number>();
  for (const run of runs.values()) {
    const keyed = run.map((p) => ({ p, hash: mixHash(`${seed}|${p.id ?? ''}`) }));
    keyed.sort((a, b) => a.hash - b.hash || compareCodeUnits(a.p.id ?? '', b.p.id ?? ''));
    keyed.forEach(({ p }, i) => round.set(p, i));
  }
  const sellerPlace = new Map<string, number>();
  for (const p of list) {
    const seller = sellerOf(p);
    if (!sellerPlace.has(seller)) sellerPlace.set(seller, mixHash(`${seed}|${seller}`));
  }
  return list.sort((a, b) =>
    (b.sales ?? 0) - (a.sales ?? 0)
    || round.get(a)! - round.get(b)!
    || sellerPlace.get(sellerOf(a))! - sellerPlace.get(sellerOf(b))!
    || compareCodeUnits(sellerOf(a), sellerOf(b)),
  );
}

/**
 * Order a list. Returns a NEW array; the input is never sorted in place, which
 * matters because both callers hand in a memoised list other memos also read.
 *
 * @param mixSeed Best-selling only: deal tied listings round their sellers in
 *   this seed's order (bestSellingMixed). The marketplace and search pass
 *   browseMixSeed's; a storefront, one seller's own list, passes none and keeps
 *   its ties in the order it handed in.
 */
export function sortProducts<P extends FilterableProduct>(
  products: readonly P[],
  sortBy: ProductSort,
  mixSeed?: string,
): P[] {
  const list = [...products];
  if (sortBy === 'best-selling' && mixSeed !== undefined) return bestSellingMixed(list, mixSeed);
  switch (sortBy) {
    case 'newest':
      return list.sort((a, b) => productReleaseMs(b) - productReleaseMs(a));
    case 'top-rated':
      return list.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
    case 'price-asc':
      return list.sort((a, b) => effectivePriceAud(a) - effectivePriceAud(b));
    case 'price-desc':
      return list.sort((a, b) => effectivePriceAud(b) - effectivePriceAud(a));
    case 'best-selling':
    default:
      return list.sort((a, b) => (b.sales ?? 0) - (a.sales ?? 0));
  }
}

/**
 * The visible text of a rich-text field, with its markup removed.
 *
 * `longDescription` is SANITIZED HTML, not prose: it is written by the Tiptap
 * editor and rendered through `dangerouslySetInnerHTML` on the product page.
 * Matching a search against it raw searched the markup as well as the words,
 * and failed in both directions. Every tag name, attribute name and embedded
 * storage URL was searchable text, so "img src", "googleapis" and
 * "firebasestorage" each matched every product carrying a description image.
 * Meanwhile a phrase the seller had split across a bold run or a link
 * ("the <strong>best</strong> pack") could not be found at all.
 *
 * Tags become a SPACE rather than nothing, which is the half that fixes the
 * second failure: without it `<p>alpha</p><p>beta</p>` collapses to
 * "alphabeta" and invents a match for "phabet" that no reader can see.
 *
 * Entities are decoded for the five that the sanitizer actually emits, so a
 * seller who typed "Tom & Jerry" is findable by "tom & jerry" rather than only
 * by "tom &amp; jerry".
 */
export function htmlToSearchText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#(?:39|x27);/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Everything a product is searchable BY, lowercased and joined.
 *
 * Memoised against the product object, because stripping the markup out of a
 * description is far too expensive to redo per keystroke: the marketplace runs
 * this over the whole catalogue on every character typed, and a description is
 * kilobytes of HTML. A `WeakMap` rather than a keyed cache so an entry dies
 * with the product it describes and nothing has to be evicted.
 *
 * Keyed on IDENTITY, which is sound here because nothing mutates a product in
 * place: the context rebuilds the object (`{ ...product }`) whenever a field
 * changes, so an edited product is a new key and gets a fresh haystack.
 */
const searchTextCache = new WeakMap<object, string>();

/**
 * A product's search haystack, exported for the server's /search first screen
 * (src/lib/search-first-screen.ts), which builds it once per product when it
 * reads the catalogue and must match exactly what filterProducts matches here.
 */
export function productSearchText(product: FilterableProduct): string {
  return searchTextOf(product);
}

/** The search box's text as filterProducts matches it: trimmed and lowercased; '' matches everything. */
export function searchNeedle(search: string): string {
  return search.trim().toLowerCase();
}

function searchTextOf(product: FilterableProduct): string {
  const cached = searchTextCache.get(product as object);
  if (cached !== undefined) return cached;
  const text = [
    product.name || '',
    product.shortDescription || '',
    htmlToSearchText(product.longDescription || ''),
    product.aiHint || '',
    ...searchTagsOf(product),
    ...taxonomySearchWords(product),
    // Joined on a NEWLINE, not a space. The fields are concatenated into one
    // haystack so the match is a single `includes`, and a space separator would
    // let a query straddle two of them: a product named "Winter Pack" described
    // as "Summer sale" would answer to "pack summer", which appears nowhere a
    // reader can see it. A search box cannot contain a newline, so no real
    // query can span the seam.
  ].join('\n').toLowerCase();
  searchTextCache.set(product as object, text);
  return text;
}

/**
 * Whether a product matches the search text.
 *
 * Name, both descriptions, the AI hint and the tags. `query` arrives already
 * lowercased: with a thousand products and five string fields each, lowercasing
 * inside the loop is five thousand `toLowerCase` calls per keystroke.
 */
function matchesSearch(product: FilterableProduct, query: string): boolean {
  if (!query) return true;
  return searchTextOf(product).includes(query);
}

function matchesRecency(product: FilterableProduct, state: ProductFilterState): boolean {
  if (state.recency === 'all-time') return true;
  const releaseMs = productReleaseMs(product);
  // A product with no usable date passes every window rather than vanishing
  // from all of them. Hiding a listing because its own document is missing a
  // field the seller never chose is a worse failure than showing it early.
  if (!releaseMs) return true;

  if (state.recency === 'custom') {
    const from = state.customDateRange?.from;
    const to = state.customDateRange?.to ?? state.customDateRange?.from;
    // A half-picked range filters nothing, so the grid does not empty itself
    // between the two clicks it takes to choose one.
    if (!from || !to) return true;
    const toEnd = new Date(to);
    toEnd.setHours(23, 59, 59, 999);
    return releaseMs >= from.getTime() && releaseMs <= toEnd.getTime();
  }

  const days = RECENCY_DAYS[state.recency];
  if (!days) return true;
  return releaseMs >= Date.now() - days * 24 * 60 * 60 * 1000;
}

/**
 * Apply every filter. Tags and file types are AND, not OR: ticking two tags
 * asks for products carrying both, which is what the compound facet counts
 * beside them promise. Types are OR, because a product has exactly one: two
 * ticked types asking for both would always be empty. The purpose bar matches
 * a product's main purpose or its second.
 */
export function filterProducts<P extends FilterableProduct>(
  products: readonly P[],
  state: ProductFilterState,
): P[] {
  const query = searchNeedle(state.search);
  const purpose = state.purpose || null;
  const types = state.types ?? [];
  const fieldsWanted = hasCategoryFields(state.categoryFields);
  return products.filter((product) => {
    if (!matchesSearch(product, query)) return false;

    if (purpose && !effectiveTaxonomyOf(product).purposes.includes(purpose)) return false;

    if (types.length > 0 && !types.includes(effectiveTaxonomyOf(product).catalogType)) return false;

    if (fieldsWanted && !matchesCategoryFields(categoryFieldsOf(product), state.categoryFields)) return false;

    if (state.tags.length > 0) {
      const tags = filterTagsOf(product);
      if (!state.tags.every((t) => tags.includes(t))) return false;
    }

    if (state.fileTypes.length > 0) {
      const exts = productFileExtsOf(product);
      if (!state.fileTypes.every((ft) => exts.includes(ft))) return false;
    }

    const price = effectivePriceAud(product);
    if (price < state.priceRange[0] || price > state.priceRange[1]) return false;

    if ((product.rating ?? 0) < state.rating) return false;

    if (state.language && state.language !== 'all') {
      const lang = (product.details as { language?: { value?: unknown } } | null | undefined)?.language?.value;
      if (lang !== state.language) return false;
    }

    return matchesRecency(product, state);
  });
}

export type Facet = { name: string; count: number };

/**
 * Tag options, counted over the products that already match every SELECTED tag.
 *
 * Compound rather than global, so ticking a second tag offers only tags that
 * co-occur with the first and every count shown is a count of what ticking it
 * would actually leave. A global facet list offers combinations that resolve to
 * an empty grid.
 */
export function tagFacets(products: readonly FilterableProduct[], selectedTags: readonly string[]): Facet[] {
  const pool = selectedTags.length === 0
    ? products
    : products.filter((p) => selectedTags.every((t) => filterTagsOf(p).includes(t)));
  const counts = new Map<string, number>();
  for (const product of pool) {
    for (const tag of filterTagsOf(product)) {
      counts.set(tag, (counts.get(tag) || 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count }));
}

/**
 * Tag options counted over the products that match EVERY filter in `state`,
 * not only the ticked tags: the search, the purpose, the types, the classroom
 * fields, file types, price, rating, recency and language too. So each count
 * is what ticking that tag would leave in the grid, whatever else is narrowing
 * it (the marketplace's Tags menu).
 *
 * A ticked tag that nothing matches any more stays offered, at 0, so it can
 * still be unticked from the menu, and the Tags button (shown only while there
 * are options) does not vanish with it.
 */
export function filteredTagFacets(products: readonly FilterableProduct[], state: ProductFilterState): Facet[] {
  const facets = tagFacets(filterProducts(products, state), []);
  for (const tag of state.tags) {
    if (!facets.some((facet) => facet.name === tag)) facets.push({ name: tag, count: 0 });
  }
  return facets;
}

/** File-type options, counted the same compound way, one count per product. */
export function fileTypeFacets(
  products: readonly FilterableProduct[],
  selectedFileTypes: readonly string[],
): Facet[] {
  const pool = selectedFileTypes.length === 0
    ? products
    : products.filter((p) => selectedFileTypes.every((ft) => productFileExtsOf(p).includes(ft)));
  const counts = new Map<string, number>();
  for (const product of pool) {
    for (const ext of new Set(productFileExtsOf(product))) {
      counts.set(ext, (counts.get(ext) || 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count }));
}

/**
 * The Filters menu's type options: every type in list order, each with how
 * many products are of it. Every type is listed, empty or not, as the owner
 * asked for the whole list; the menu shows a count only where there is one.
 */
export function catalogTypeFacets(products: readonly FilterableProduct[]): Facet[] {
  const counts = new Map<string, number>();
  for (const product of products) {
    const type = effectiveTaxonomyOf(product).catalogType;
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }
  return CATALOG_TYPE_SLUGS.map((name) => ({ name, count: counts.get(name) ?? 0 }));
}

/**
 * The top of the price slider, in AUD.
 *
 * 500 for an empty list, so the slider still has a scale to drag along on a
 * storefront whose products have not loaded yet.
 */
export function maxProductPrice(products: readonly FilterableProduct[]): number {
  if (products.length === 0) return 500;
  return Math.ceil(Math.max(...products.map(effectivePriceAud)));
}
